import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Footer from '../components/Footer';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://templimizeai-coursecreatorvault.onrender.com/api';

interface User {
  id: string;
  name: string;
  email: string;
  tier: string;
  createdAt: string;
  courseCount?: number;
  courses?: any[];
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  subscriptionStatus?: string;
  tierUpdatedAt?: string;
}

interface Stats {
  totalUsers: number;
  freeUsers: number;
  basicUsers: number;
  premiumUsers: number;
  totalCourses: number;
  newUsersThisWeek: number;
  newUsersThisMonth: number;
  recentSignups: User[];
  activeSubscriptions?: number;
  monthlyRevenue?: number;
}

interface Course {
  id: string;
  title: string;
  niche: string;
  status: string;
  createdAt: string;
  ownerName: string;
  ownerEmail: string;
  ownerTier: string;
}

const Admin: React.FC = () => {
  const { token, logout, user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState<'dashboard' | 'users' | 'courses' | 'campaigns'>('dashboard');
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);

  // Email campaign state
  const [campaignSubject, setCampaignSubject] = useState('');
  const [campaignContent, setCampaignContent] = useState('');
  const [campaignTierFilter, setCampaignTierFilter] = useState('all');
  const [sendingCampaign, setSendingCampaign] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchUsers();
    fetchAllCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error: any) {
      console.error('Failed to fetch stats:', error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setUnauthorized(true);
        toast.error('Access denied: Admin privileges required');
      } else {
        toast.error('Failed to load statistics');
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setUnauthorized(true);
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data.courses);
    } catch (error: any) {
      console.error('Failed to fetch courses:', error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setUnauthorized(true);
      } else {
        toast.error('Failed to load courses');
      }
    }
  };

  const fetchUserDetails = async (userId: string) => {
    try {
      const response = await axios.get(`${API_URL}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUser(response.data.user);
      setShowUserModal(true);
    } catch (error: any) {
      console.error('Failed to fetch user details:', error);
      toast.error('Failed to load user details');
    }
  };

  const handleChangeTier = async (userId: string, newTier: string) => {
    if (!window.confirm(`Are you sure you want to change this user's tier to ${newTier.toUpperCase()}?`)) {
      return;
    }

    try {
      await axios.put(
        `${API_URL}/admin/users/${userId}/tier`,
        { tier: newTier },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User tier changed to ${newTier.toUpperCase()}`);
      fetchUsers();
      fetchStats();
      if (selectedUser?.id === userId) {
        fetchUserDetails(userId);
      }
    } catch (error: any) {
      console.error('Failed to change tier:', error);
      toast.error(error.response?.data?.error || 'Failed to change tier');
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (!window.confirm('Are you sure you want to reset this user\'s password? A temporary password will be generated.')) {
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/admin/users/${userId}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const tempPassword = response.data.temporaryPassword;
      alert(`Password reset successfully!\n\nTemporary Password: ${tempPassword}\n\nPlease share this with the user securely. They should change it after logging in.`);

      // Copy to clipboard
      navigator.clipboard.writeText(tempPassword);
      toast.success('Temporary password copied to clipboard!');
    } catch (error: any) {
      console.error('Failed to reset password:', error);
      toast.error(error.response?.data?.error || 'Failed to reset password');
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    if (!window.confirm(`Are you sure you want to DELETE user ${userEmail}? This action CANNOT be undone!`)) {
      return;
    }

    if (!window.confirm('This will permanently delete all their data. Are you ABSOLUTELY sure?')) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User deleted successfully');
      fetchUsers();
      fetchStats();
      fetchAllCourses();
      setShowUserModal(false);
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleSendCampaign = async () => {
    if (!campaignSubject.trim() || !campaignContent.trim()) {
      toast.error('Please enter both subject and content');
      return;
    }

    const recipientCount = campaignTierFilter === 'all'
      ? users.length
      : users.filter(u => u.tier === campaignTierFilter).length;

    if (!window.confirm(`Send this email to ${recipientCount} users? This cannot be undone!`)) {
      return;
    }

    setSendingCampaign(true);

    try {
      const response = await axios.post(
        `${API_URL}/admin/send-campaign`,
        {
          subject: campaignSubject,
          content: campaignContent,
          tierFilter: campaignTierFilter
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Campaign sent to ${response.data.recipientCount} users!`);
      setCampaignSubject('');
      setCampaignContent('');
      setCampaignTierFilter('all');
    } catch (error: any) {
      console.error('Failed to send campaign:', error);
      toast.error(error.response?.data?.error || 'Failed to send campaign');
    } finally {
      setSendingCampaign(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTier = tierFilter === 'all' || user.tier === tierFilter;
    return matchesSearch && matchesTier;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTierBadgeColor = (tier: string) => {
    switch (tier) {
      case 'premium':
        return 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-yellow-900';
      case 'basic':
        return 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white';
      default:
        return 'bg-gray-200 text-gray-700';
    }
  };

  // Show unauthorized error page if user doesn't have admin access
  if (unauthorized) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 px-4">
        <div className="max-w-2xl w-full text-center">
          <div className="text-8xl mb-6">🚫</div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Looks like you're in the wrong place!
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            You need admin privileges to access this area.
          </p>

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">What can you do?</h2>
            <div className="space-y-4 text-left">
              <div className="flex items-start space-x-4">
                <div className="text-2xl">👤</div>
                <div>
                  <h3 className="font-semibold text-gray-900">Not signed in?</h3>
                  <p className="text-gray-600 text-sm">Sign in with an admin account to continue</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="text-2xl">🏠</div>
                <div>
                  <h3 className="font-semibold text-gray-900">Want to use the app?</h3>
                  <p className="text-gray-600 text-sm">Go back to your dashboard to create courses</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="text-2xl">📧</div>
                <div>
                  <h3 className="font-semibold text-gray-900">Need admin access?</h3>
                  <p className="text-gray-600 text-sm">Contact support if you believe you should have access</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate('/login')}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-xl transform hover:scale-105 transition"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="border-2 border-indigo-600 text-indigo-600 px-8 py-3 rounded-lg font-semibold hover:bg-indigo-50 transition"
            >
              Go to Dashboard
            </button>
            <button
              onClick={() => navigate('/')}
              className="border-2 border-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition"
            >
              Go Home
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-8">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-red-600 via-orange-600 to-yellow-600 bg-clip-text text-transparent">
                🛡️ Admin Panel
              </h1>
              <nav className="hidden md:flex space-x-6">
                <button onClick={() => navigate('/dashboard')} className="text-gray-600 hover:text-gray-900">← Back to Dashboard</button>
              </nav>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={logout}
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'dashboard'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📊 Dashboard
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'users'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              👥 Users ({users.length})
            </button>
            <button
              onClick={() => setActiveTab('courses')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'courses'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📚 All Courses ({courses.length})
            </button>
            <button
              onClick={() => setActiveTab('campaigns')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'campaigns'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📧 Email Campaigns
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading admin data...</p>
          </div>
        ) : (
          <>
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && stats && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Overview</h2>

                  {/* Revenue Banner */}
                  <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-xl p-6 mb-8 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm mb-2">💰 Estimated Monthly Recurring Revenue</p>
                        <p className="text-4xl font-bold">
                          ${((stats.basicUsers * 29) + (stats.premiumUsers * 99)).toLocaleString()}
                        </p>
                        <p className="text-green-100 text-sm mt-2">
                          {stats.basicUsers + stats.premiumUsers} active paid subscriptions
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="bg-white bg-opacity-20 rounded-lg p-4">
                          <p className="text-2xl font-bold">{stats.basicUsers + stats.premiumUsers}</p>
                          <p className="text-sm text-green-100">Paid Users</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Total Users</p>
                          <p className="text-3xl font-bold text-gray-900">{stats.totalUsers}</p>
                        </div>
                        <div className="bg-indigo-100 p-3 rounded-lg">
                          <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Total Courses</p>
                          <p className="text-3xl font-bold text-gray-900">{stats.totalCourses}</p>
                        </div>
                        <div className="bg-purple-100 p-3 rounded-lg">
                          <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">New (This Week)</p>
                          <p className="text-3xl font-bold text-gray-900">{stats.newUsersThisWeek}</p>
                        </div>
                        <div className="bg-green-100 p-3 rounded-lg">
                          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Paid Users</p>
                          <p className="text-3xl font-bold text-gray-900">{stats.basicUsers + stats.premiumUsers}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Basic: {stats.basicUsers} | Premium: {stats.premiumUsers}
                          </p>
                        </div>
                        <div className="bg-yellow-100 p-3 rounded-lg">
                          <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <button
                        onClick={() => navigate('/admin/freebies')}
                        className="bg-gradient-to-br from-violet-500 to-purple-600 text-white p-4 rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition text-center"
                      >
                        <div className="text-2xl mb-1">🎁</div>
                        <p className="font-semibold text-sm">Manage Freebies</p>
                      </button>
                      <button
                        onClick={() => setActiveTab('users')}
                        className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white p-4 rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition text-center"
                      >
                        <div className="text-2xl mb-1">👥</div>
                        <p className="font-semibold text-sm">Manage Users</p>
                      </button>
                      <button
                        onClick={() => setActiveTab('campaigns')}
                        className="bg-gradient-to-br from-pink-500 to-rose-600 text-white p-4 rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition text-center"
                      >
                        <div className="text-2xl mb-1">📧</div>
                        <p className="font-semibold text-sm">Email Campaign</p>
                      </button>
                      <button
                        onClick={() => setActiveTab('courses')}
                        className="bg-gradient-to-br from-emerald-500 to-green-600 text-white p-4 rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition text-center"
                      >
                        <div className="text-2xl mb-1">📚</div>
                        <p className="font-semibold text-sm">View Courses</p>
                      </button>
                    </div>
                  </div>

                  {/* Recent Signups */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Signups</h3>
                    <div className="space-y-3">
                      {stats?.recentSignups?.map((user) => (
                        <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{user.name}</p>
                              <p className="text-xs text-gray-500">{user.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTierBadgeColor(user.tier)}`}>
                              {user.tier.toUpperCase()}
                            </span>
                            <p className="text-xs text-gray-500">{formatDate(user.createdAt)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <select
                        value={tierFilter}
                        onChange={(e) => setTierFilter(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                      >
                        <option value="all">All Tiers</option>
                        <option value="free">Free</option>
                        <option value="basic">Basic</option>
                        <option value="premium">Premium</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Courses</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredUsers.map((user) => (
                          <tr key={user.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                                  {user.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                  <div className="text-xs text-gray-500">ID: {user.id}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{user.email}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getTierBadgeColor(user.tier)}`}>
                                {user.tier.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {user.courseCount || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(user.createdAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => fetchUserDetails(user.id)}
                                className="text-indigo-600 hover:text-indigo-900 mr-4"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {filteredUsers.length === 0 && (
                    <div className="text-center py-12">
                      <p className="text-gray-500">No users found</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Courses Tab */}
            {activeTab === 'courses' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">All Courses</h2>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course Title</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Niche</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {courses.map((course) => (
                          <tr key={course.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{course.title}</div>
                              <div className="text-xs text-gray-500">ID: {course.id}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{course.ownerName}</div>
                              <div className="text-xs text-gray-500">{course.ownerEmail}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                                {course.niche}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-medium rounded ${
                                course.status === 'published'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {course.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(course.createdAt)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {courses.length === 0 && (
                    <div className="text-center py-12">
                      <p className="text-gray-500">No courses found</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Email Campaigns Tab */}
            {activeTab === 'campaigns' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">Email Campaigns</h2>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">📧 Broadcast to Your Users</h3>
                  <p className="text-gray-600 mb-4">
                    Send emails to all users or filter by tier. Perfect for announcements, upsells, and weekly updates!
                  </p>
                </div>

                {/* Campaign Form */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Campaign</h3>

                  {/* Tier Filter */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Send To
                    </label>
                    <select
                      value={campaignTierFilter}
                      onChange={(e) => setCampaignTierFilter(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                    >
                      <option value="all">All Users ({users.length})</option>
                      <option value="free">Free Tier Only ({users.filter(u => u.tier === 'free').length})</option>
                      <option value="basic">Basic Tier Only ({users.filter(u => u.tier === 'basic').length})</option>
                      <option value="premium">Premium Tier Only ({users.filter(u => u.tier === 'premium').length})</option>
                    </select>
                  </div>

                  {/* Subject */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Subject
                    </label>
                    <input
                      type="text"
                      value={campaignSubject}
                      onChange={(e) => setCampaignSubject(e.target.value)}
                      placeholder="🎉 Exciting News from Templimize AI!"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                    />
                  </div>

                  {/* Content */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Content (HTML supported)
                    </label>
                    <textarea
                      value={campaignContent}
                      onChange={(e) => setCampaignContent(e.target.value)}
                      placeholder="<h2>Hey there!</h2><p>We've got something exciting to share...</p>"
                      rows={10}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent font-mono text-sm"
                    />
                  </div>

                  {/* Send Button */}
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                      Recipients: <strong>
                        {campaignTierFilter === 'all' ? users.length : users.filter(u => u.tier === campaignTierFilter).length}
                      </strong> users
                    </p>
                    <button
                      onClick={handleSendCampaign}
                      disabled={sendingCampaign || !campaignSubject.trim() || !campaignContent.trim()}
                      className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {sendingCampaign ? 'Sending...' : '📤 Send Campaign'}
                    </button>
                  </div>
                </div>

                {/* Quick Templates */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">📝 Quick Templates</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <button
                      onClick={() => {
                        setCampaignSubject('🚀 Upgrade to Premium and Save 30%!');
                        setCampaignContent(`<h2>Hi there!</h2><p>We noticed you're loving Templimize AI on the Free tier. Ready to unlock the full power of AI course creation?</p><p><strong>For a limited time, get 30% off Premium!</strong></p><ul><li>✨ Complete video course generation</li><li>🎯 Unlimited trending topic discovery</li><li>📝 Interactive quizzes</li><li>🎨 Sales page builder</li></ul><p><a href="https://app.templimizeai.com/dashboard" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; font-weight: bold;">Upgrade Now →</a></p>`);
                        setCampaignTierFilter('free');
                      }}
                      className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition text-left"
                    >
                      <p className="font-semibold text-gray-900">💰 Upsell to Premium</p>
                      <p className="text-sm text-gray-600">Convert free users to paid</p>
                    </button>

                    <button
                      onClick={() => {
                        setCampaignSubject('🎁 Your Weekly Elite Bonus Inside!');
                        setCampaignContent(`<h2>Hey Premium Member!</h2><p>Your exclusive weekly bonus is here! 🎉</p><p>This week: <strong>50 Pre-Made Course Templates</strong> in the hottest niches.</p><p><a href="https://app.templimizeai.com/templates" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; font-weight: bold;">Download Now →</a></p><p>Keep crushing it!<br/>Sloane</p>`);
                        setCampaignTierFilter('premium');
                      }}
                      className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition text-left"
                    >
                      <p className="font-semibold text-gray-900">🎁 Premium Weekly Bonus</p>
                      <p className="text-sm text-gray-600">Send freebies to Elite users</p>
                    </button>

                    <button
                      onClick={() => {
                        setCampaignSubject('📢 Important: New Features Available!');
                        setCampaignContent(`<h2>New Features Just Dropped! 🚀</h2><p>We've been working hard to make Templimize AI even better. Check out what's new:</p><ul><li>✨ Enhanced AI course generation</li><li>📊 New analytics dashboard</li><li>🎨 More template options</li></ul><p><a href="https://app.templimizeai.com/dashboard" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; font-weight: bold;">Check It Out →</a></p>`);
                        setCampaignTierFilter('all');
                      }}
                      className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition text-left"
                    >
                      <p className="font-semibold text-gray-900">📢 Announcement</p>
                      <p className="text-sm text-gray-600">Share news with everyone</p>
                    </button>

                    <button
                      onClick={() => {
                        setCampaignSubject('💡 Tips: Get the Most Out of Templimize AI');
                        setCampaignContent(`<h2>Pro Tips for Course Creators! 💡</h2><p>Want to create courses faster? Here are some insider tips:</p><ol><li><strong>Start with trending topics</strong> - Let AI discover what's hot</li><li><strong>Use templates</strong> - Speed up your workflow</li><li><strong>Export early</strong> - Save your progress often</li></ol><p>Have questions? Just reply to this email!</p><p>Happy creating!<br/>Sloane</p>`);
                        setCampaignTierFilter('all');
                      }}
                      className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition text-left"
                    >
                      <p className="font-semibold text-gray-900">💡 Tips & Tricks</p>
                      <p className="text-sm text-gray-600">Help users succeed</p>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* User Detail Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{selectedUser.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{selectedUser.email}</p>
                </div>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* User Info */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">User Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">User ID</p>
                    <p className="text-sm font-medium text-gray-900">{selectedUser.id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Current Tier</p>
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getTierBadgeColor(selectedUser.tier)}`}>
                      {selectedUser.tier.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Join Date</p>
                    <p className="text-sm font-medium text-gray-900">{formatDate(selectedUser.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Courses</p>
                    <p className="text-sm font-medium text-gray-900">{selectedUser.courses?.length || 0}</p>
                  </div>
                </div>
              </div>

              {/* Subscription Details */}
              {(selectedUser.tier === 'basic' || selectedUser.tier === 'premium' || selectedUser.stripeCustomerId) && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">💳 Subscription Details</h4>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Subscription Status</p>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedUser.subscriptionStatus ? (
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              selectedUser.subscriptionStatus === 'active'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {selectedUser.subscriptionStatus.toUpperCase()}
                            </span>
                          ) : (
                            <span className="text-gray-500">Manual Upgrade</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Tier Updated</p>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedUser.tierUpdatedAt ? formatDate(selectedUser.tierUpdatedAt) : 'N/A'}
                        </p>
                      </div>
                      {selectedUser.stripeCustomerId && (
                        <div className="col-span-2">
                          <p className="text-xs text-gray-500">Stripe Customer ID</p>
                          <p className="text-sm font-mono text-gray-700 break-all">{selectedUser.stripeCustomerId}</p>
                        </div>
                      )}
                      {selectedUser.stripeSubscriptionId && (
                        <div className="col-span-2">
                          <p className="text-xs text-gray-500">Stripe Subscription ID</p>
                          <p className="text-sm font-mono text-gray-700 break-all">{selectedUser.stripeSubscriptionId}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Admin Actions */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Admin Actions</h4>
                <div className="space-y-3">
                  {/* Change Tier */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Change Tier</p>
                      <p className="text-xs text-gray-500">Upgrade or downgrade user subscription</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleChangeTier(selectedUser.id, 'free')}
                        disabled={selectedUser.tier === 'free'}
                        className="px-3 py-1 text-xs font-medium bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
                      >
                        Free
                      </button>
                      <button
                        onClick={() => handleChangeTier(selectedUser.id, 'basic')}
                        disabled={selectedUser.tier === 'basic'}
                        className="px-3 py-1 text-xs font-medium bg-indigo-500 text-white rounded hover:bg-indigo-600 disabled:opacity-50"
                      >
                        Basic
                      </button>
                      <button
                        onClick={() => handleChangeTier(selectedUser.id, 'premium')}
                        disabled={selectedUser.tier === 'premium'}
                        className="px-3 py-1 text-xs font-medium bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50"
                      >
                        Premium
                      </button>
                    </div>
                  </div>

                  {/* Reset Password */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Reset Password</p>
                      <p className="text-xs text-gray-500">Generate temporary password for user</p>
                    </div>
                    <button
                      onClick={() => handleResetPassword(selectedUser.id)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                    >
                      Reset Password
                    </button>
                  </div>

                  {/* Delete User */}
                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                    <div>
                      <p className="text-sm font-medium text-red-900">Delete User</p>
                      <p className="text-xs text-red-600">Permanently delete user and all their data</p>
                    </div>
                    <button
                      onClick={() => handleDeleteUser(selectedUser.id, selectedUser.email)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>

              {/* User's Courses */}
              {selectedUser.courses && selectedUser.courses.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">User's Courses ({selectedUser.courses.length})</h4>
                  <div className="space-y-2">
                    {selectedUser.courses.map((course: any) => (
                      <div key={course.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{course.title}</p>
                            <p className="text-xs text-gray-500">Niche: {course.niche}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            course.status === 'published'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {course.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Admin;
